/**
 * portfolioStats — pure computation utilities for portfolio analytics.
 * No React / Zustand dependencies; pass raw data in, get metrics out.
 */

import type { Order, Position, EquityPoint } from '@arc/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TradeMetrics {
  totalTrades:   number;
  winTrades:     number;
  lossTrades:    number;
  winRate:       number;        // 0–100
  totalPnl:      number;
  avgWin:        number;
  avgLoss:       number;
  profitFactor:  number;        // gross profit / gross loss
  sharpeRatio:   number;        // simplified: mean(returns) / std(returns) * sqrt(252)
  maxDrawdown:   number;        // max peak-to-trough $ drawdown in equity curve
  maxDrawdownPct: number;       // same as %
  bestTrade:     number;
  worstTrade:    number;
  avgHoldingHrs: number;
}

export interface SymbolMetrics extends TradeMetrics {
  symbol: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stddev(values: number[]): number {
  if (values.length < 2) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

// ─── Core metrics from filled orders ─────────────────────────────────────────

export function calcTradeMetrics(orders: Order[]): TradeMetrics {
  const filled = orders.filter(o => o.status === 'filled' && o.pnl !== undefined);

  const totalTrades = filled.length;
  if (totalTrades === 0) {
    return {
      totalTrades: 0, winTrades: 0, lossTrades: 0,
      winRate: 0, totalPnl: 0, avgWin: 0, avgLoss: 0,
      profitFactor: 0, sharpeRatio: 0,
      maxDrawdown: 0, maxDrawdownPct: 0,
      bestTrade: 0, worstTrade: 0, avgHoldingHrs: 0,
    };
  }

  const pnls = filled.map(o => o.pnl!);
  const wins = pnls.filter(p => p >= 0);
  const losses = pnls.filter(p => p < 0);

  const grossProfit = wins.reduce((s, p) => s + p, 0);
  const grossLoss   = Math.abs(losses.reduce((s, p) => s + p, 0));

  // Holding time (ms → hours)
  const holdingHrs = filled
    .filter(o => o.closedAt !== undefined)
    .map(o => ((o.closedAt ?? o.timestamp) - o.timestamp) / 3_600_000);
  const avgHoldingHrs = holdingHrs.length > 0
    ? holdingHrs.reduce((a, b) => a + b, 0) / holdingHrs.length
    : 0;

  // Simplified daily Sharpe (assumes each trade = 1 period)
  const pnlStd = stddev(pnls);
  const pnlMean = pnls.reduce((a, b) => a + b, 0) / pnls.length;
  const sharpeRatio = pnlStd > 0 ? (pnlMean / pnlStd) * Math.sqrt(252) : 0;

  return {
    totalTrades,
    winTrades:    wins.length,
    lossTrades:   losses.length,
    winRate:      Math.round(wins.length / totalTrades * 100),
    totalPnl:     pnls.reduce((s, p) => s + p, 0),
    avgWin:       wins.length   > 0 ? grossProfit / wins.length   : 0,
    avgLoss:      losses.length > 0 ? grossLoss   / losses.length : 0,
    profitFactor: grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0,
    sharpeRatio:  parseFloat(sharpeRatio.toFixed(2)),
    maxDrawdown:    0, // computed separately from equity curve
    maxDrawdownPct: 0,
    bestTrade:  Math.max(...pnls),
    worstTrade: Math.min(...pnls),
    avgHoldingHrs: parseFloat(avgHoldingHrs.toFixed(1)),
  };
}

// ─── Max drawdown from equity curve ──────────────────────────────────────────

export function calcMaxDrawdown(curve: EquityPoint[]): { drawdown: number; drawdownPct: number } {
  if (curve.length < 2) return { drawdown: 0, drawdownPct: 0 };

  let peak   = curve[0]!.equity;
  let maxDD  = 0;
  let maxPct = 0;

  for (const pt of curve) {
    if (pt.equity > peak) peak = pt.equity;
    const dd    = peak - pt.equity;
    const ddPct = peak > 0 ? dd / peak * 100 : 0;
    if (dd    > maxDD)  maxDD  = dd;
    if (ddPct > maxPct) maxPct = ddPct;
  }

  return {
    drawdown:    parseFloat(maxDD.toFixed(2)),
    drawdownPct: parseFloat(maxPct.toFixed(2)),
  };
}

// ─── Per-symbol breakdown ─────────────────────────────────────────────────────

export function calcSymbolMetrics(orders: Order[]): SymbolMetrics[] {
  const bySymbol: Record<string, Order[]> = {};
  for (const o of orders) {
    if (!bySymbol[o.symbol]) bySymbol[o.symbol] = [];
    bySymbol[o.symbol]!.push(o);
  }

  return Object.entries(bySymbol).map(([symbol, ords]) => ({
    symbol,
    ...calcTradeMetrics(ords),
  })).sort((a, b) => b.totalPnl - a.totalPnl);
}

// ─── Open positions summary ───────────────────────────────────────────────────

export function calcOpenSummary(positions: Position[]): {
  totalSize:    number;
  totalPnl:     number;
  longCount:    number;
  shortCount:   number;
  biggestWin:   number;
  biggestLoss:  number;
} {
  const totalSize   = positions.reduce((s, p) => s + p.size, 0);
  const totalPnl    = positions.reduce((s, p) => s + p.unrealizedPnl, 0);
  const longCount   = positions.filter(p => p.side === 'buy').length;
  const shortCount  = positions.filter(p => p.side === 'sell').length;
  const pnls        = positions.map(p => p.unrealizedPnl);
  const biggestWin  = pnls.length > 0 ? Math.max(...pnls) : 0;
  const biggestLoss = pnls.length > 0 ? Math.min(...pnls) : 0;
  return { totalSize, totalPnl, longCount, shortCount, biggestWin, biggestLoss };
}
